import { apiService } from "@/services";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { handleApiError } from "@/utils";
import { Organization, OrganizationsResponse, GetOrganizationsParams, OrganizationPayload } from "@/types";
import { DEFAULT_STALE_TIME } from "@/constants";

export const useGetOrganizations = (params: GetOrganizationsParams = {}) =>
  useQuery({
    queryKey: ['organizations', params],
    queryFn: () => {
      const searchParams = new URLSearchParams();
      if (params.search) searchParams.set('search', params.search);
      if (params.page) searchParams.set('page', String(params.page));
      const qs = searchParams.toString();
      return apiService.get<OrganizationsResponse, unknown>(`/organizations${qs ? `?${qs}` : ''}`);
    },
    staleTime: DEFAULT_STALE_TIME,
  });

export const useCreateOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation<Organization, unknown, OrganizationPayload>({
    mutationFn: (data) => apiService.post('/organizations', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
    onError: (error) => handleApiError(error),
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation<Organization, unknown, { id: number; data: OrganizationPayload }>({
    mutationFn: ({ id, data }) => apiService.put(`/organizations/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
    onError: (error) => handleApiError(error),
  });
};

export const useDeleteOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, number>({
    mutationFn: (id) => apiService.delete(`/organizations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
    onError: (error) => handleApiError(error),
  });
};
